'use client';

import { ArrowRight } from 'lucide-react';
import { kickerStyle, sectionInner } from '../_shared/styles';
import { Button } from '@akademi/ui';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Hero banner — full-width opener with optional background image,
 * kicker, title, subtitle, and CTA. Pure presentational; no data fetch.
 *
 * <p>Direct port of ThemeMaker's
 * {@code blocks/hero/HeroBanner/HeroBanner.server.tsx}.
 *
 * <p>{@code 'use client'} only for {@link useBlockPart} (the
 * {@code data-<ns>-part} test/onboarding hooks). Still pure-render —
 * no data fetch, no state — and always rendered inside the portal's
 * client renderer anyway.
 */
export interface HeroBannerProps {
  kicker?: string;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  ctaHref?: string;
  image?: string;
  variant?: 'centered' | 'left';
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroBanner({
  kicker,
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  image,
  variant = 'centered',
  maxWidth = 'full',
}: HeroBannerProps) {
  const isLeft = variant === 'left';
  const part = useBlockPart();
  return (
    <BlockRoot
      as="section"
      style={{
        padding:
          'clamp(var(--space-8, 32px), 8vw, var(--space-16, 64px)) ' +
          'clamp(var(--space-4, 16px), 4vw, var(--space-6, 24px))',
        background: image
          ? `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${image}) center/cover`
          : 'var(--color-brand-50, #f0f4ff)',
        color: image ? '#fff' : 'inherit',
        textAlign: isLeft ? 'left' : 'center',
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <div style={{ ...sectionInner, maxWidth: 'none' }}>
        {kicker ? (
          <div
            {...part('kicker')}
            style={{
              ...kickerStyle,
              marginBottom: 'var(--space-3, 12px)',
              color: image ? 'rgba(255,255,255,0.85)' : kickerStyle.color,
              display: isLeft ? 'block' : 'inline-block',
            }}
          >
            {kicker}
          </div>
        ) : null}
        <h1
          {...part('title')}
          style={{
            margin: 0,
            fontSize: 'clamp(var(--font-size-2xl, 32px), 5vw, var(--font-size-3xl, 48px))',
            lineHeight: 'var(--line-height-tight, 1.2)',
            fontWeight: 'var(--font-weight-bold, 700)',
            fontFamily: 'var(--font-family-display, inherit)',
            color: image ? '#fff' : 'var(--color-text-primary, #0f1218)',
          }}
        >
          {title || 'Welcome'}
        </h1>
        {subtitle ? (
          <p
            {...part('subtitle')}
            style={{
              marginTop: 'var(--space-4, 16px)',
              fontSize: 'clamp(var(--font-size-md, 16px), 2vw, var(--font-size-lg, 18px))',
              color: image ? 'rgba(255,255,255,0.9)' : 'var(--color-text-secondary, #5f6675)',
            }}
          >
            {subtitle}
          </p>
        ) : null}
        {ctaLabel ? (
          <div style={{ marginTop: 'var(--space-6, 24px)' }}>
            <Button
              {...part('cta')}
              href={ctaHref ?? '#'}
              style={{
                height: 'auto',
                gap: 'var(--space-2, 8px)',
                padding: 'var(--space-3, 12px) var(--space-6, 24px)',
                fontWeight: 'var(--font-weight-bold, 700)',
              }}
            >
              {ctaLabel}
              <ArrowRight aria-hidden size={18} />
            </Button>
          </div>
        ) : null}
      </div>
    </BlockRoot>
  );
}
